import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { tokenStore } from '@/lib/api/token-store';

import { loginVerifySchema, userSchema } from './schemas';
import type {
  AuthSuccess,
  LoginPasswordRequest,
  LoginVerifyResult,
  OtpDispatch,
  SendOtpRequest,
  User,
} from './types';

function adoptSession(payload: { accessToken: string; user: unknown }): AuthSuccess {
  const { accessToken, user } = payload;
  const parsed = userSchema.parse(user);
  tokenStore.setAccessToken(accessToken);
  return { accessToken, user: parsed };
}

export async function loginWithPassword(body: LoginPasswordRequest): Promise<AuthSuccess> {
  return adoptSession(
    await apiClient.post<{ accessToken: string; user: unknown }>(endpoints.auth.login, body),
  );
}

export async function sendLoginOtp(body: SendOtpRequest): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpSend, body);
}

export async function verifyLoginOtp(body: {
  referenceId: string;
  otp: string;
}): Promise<LoginVerifyResult> {
  const data = loginVerifySchema.parse(await apiClient.post(endpoints.auth.otpVerify, body));
  if ('passwordSetToken' in data) {
    tokenStore.setActivationToken(data.passwordSetToken);
    return { kind: 'activation' };
  }
  return { kind: 'session', ...adoptSession(data) };
}

export async function sendForgotOtp(phone: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotSend, { phone });
}

export async function resendLoginOtp(referenceId: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpResend, { referenceId });
}

export async function resendForgotOtp(referenceId: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotResend, { referenceId });
}

export async function verifyForgotOtp(body: { referenceId: string; otp: string }): Promise<string> {
  const { passwordResetToken } = await apiClient.post<{ passwordResetToken: string }>(
    endpoints.auth.forgotVerify,
    body,
  );
  tokenStore.setResetToken(passwordResetToken);
  return passwordResetToken;
}

export async function resetPassword(newPassword: string): Promise<void> {
  const token = tokenStore.getResetToken();
  await apiClient.post(
    endpoints.auth.forgotReset,
    { newPassword, rememberMe: false },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
}

/** First-login activation. Returns `{ message }` only — no access token, no refresh cookie — so the
 *  caller must send the user to log in rather than adopting a session (story 101638). */
export async function setActivationPassword(newPassword: string): Promise<void> {
  const token = tokenStore.getActivationToken();
  await apiClient.post(
    endpoints.auth.activateSetPassword,
    { newPassword },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
}

export async function fetchCurrentUser(): Promise<User> {
  return userSchema.parse(await apiClient.get(endpoints.auth.me));
}

export async function logout(): Promise<void> {
  await apiClient.post(endpoints.auth.logout);
  // Only after the server has revoked the session (117853 §13e): a failure leaves the admin
  // signed in with the dialog open. A 401 never gets here — the interceptor's failed refresh
  // already cleared the store and raised `sessionExpiry`.
  tokenStore.clear();
}

/** Renew the session credential and nothing else — the keep-alive's call. `refreshSession`
 *  also re-reads `/auth/admin/me`, which a periodic timer has no reason to pay for. */
export async function renewSession(): Promise<void> {
  const { accessToken } = await apiClient.post<{ accessToken: string }>(endpoints.auth.refresh);
  tokenStore.setAccessToken(accessToken);
}

export async function refreshSession(): Promise<User> {
  const { accessToken } = await apiClient.post<{ accessToken: string }>(endpoints.auth.refresh);
  tokenStore.setAccessToken(accessToken);
  return fetchCurrentUser();
}
